import { type AllActivitiesResponse, getAllActivities } from '@repo/advanced-filters';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, Divider, Flex, Row, Typography,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import {
  type LeadActivityCreateNodeData,
  type LeadActivityCreateNodeType,
  nodeSchemas,
} from 'automations/types/nodeTypes';
import { getParsedTriggerActivityConfig } from 'automations/utils/triggeredActivityConfig';
import { saveTriggerNode } from 'automations/utils/triggerNode';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { ZodError } from 'zod';

import AutomationsCriteriaConditionsWrapper from '../AutomationsCriteriaConditionsWrapper';
import { type BuilderUiProps } from '../AutomationsCriteriaConditionsWrapper/AutomationsCriteriaConditionsWrapper';
import ShowConditionsUi from '../ShowConditionsUi';
import ActivitySelection from './ActivitySelection';
import styles from './LeadActivityCreateMenu.module.scss';

export default function LeadActivityCreateMenu({
  node,
}: {
  node: NodeProps<LeadActivityCreateNodeType>;
}) {
  const automationId = useAutomationId();
  const dispatch = useAutomationDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const { data: activitiesData } = useGetGraphqlDataQuery<UseQueryResult<AllActivitiesResponse>>({
    document: getAllActivities,
  });

  const [
    selectedActivities, setSelectedActivities,
  ] = useState<LeadActivityCreateNodeData['additionalOptions']['newActivity']['criterias']>(
    node.data?.additionalOptions?.newActivity?.criterias ?? [],
  );
  const [additionalConditionsId, setAdditionalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.criteriaGroupId ?? undefined,
  );
  const [removalConditionsId, setRemovalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.removableCriteriaGroupId ?? undefined,
  );

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    try {
      const parsedNodeData = nodeSchemas.LeadActivityCreateNodeData.parse({
        ...node.data,
        additionalOptions: {
          newActivity: {
            comparator: 'or',
            criterias: selectedActivities,
          },
          criteriaGroupId: additionalConditionsId,
          removableCriteriaGroupId: removalConditionsId,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));

      await dispatch(saveTriggerNode(node.id, automationId, contentMode));

      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(
          `Failed to update #{${TranslationEntity.automation}}`,
          [{ capitalize: true }],
        );
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [additionalConditionsId, automationId, contentMode, dispatch, node.data, node.id,
    removalConditionsId, selectedActivities, setMenuOpen,
  ]);

  const updateExistingField = useCallback((
    field: LeadActivityCreateNodeData['additionalOptions']['newActivity']['criterias'][number],
    index: number,
  ) => {
    setSelectedActivities((prev) => prev.map((item, i) => (i === index ? field : item)));
  }, []);

  const deleteField = useCallback((index: number) => {
    setSelectedActivities((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addNewDefaultField = useCallback(() => {
    if (activitiesData?.activities.length) {
      setSelectedActivities((prev) => [...prev, {
        activityId: parseInt(activitiesData.activities[0]?.value as string, 10) ?? -1,
      }]);
    }
  }, [activitiesData?.activities]);

  useEffect(() => {
    if (activitiesData?.activities.length && selectedActivities.length === 0) {
      addNewDefaultField();
    }
  }, [activitiesData?.activities, addNewDefaultField, selectedActivities.length]);

  const triggeredActivityConfig = useMemo(() => {
    if (selectedActivities.length) {
      return [getParsedTriggerActivityConfig(selectedActivities)];
    }

    return [];
  }, [selectedActivities]);

  const ConditionsBuilderUi = useCallback((props: BuilderUiProps) => (
    <ShowConditionsUi
      {...props}
      title="Set Conditions"
      btnClassName={styles.addBtn}
    />
  ), []);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `#{${TranslationEntity.automation}} Trigger - When #{${TranslationEntity.activity}} is added`,
          [{ capitalize: true }, { capitalize: true, withArticle: true }],
        ),
      }}
    >
      <Card title={contentMode === 'create' ? 'Set Trigger' : 'Edit Trigger'} className={styles.containerCard}>
        <ActivitySelection
          selectedActivities={selectedActivities}
          activitiesData={activitiesData}
          updateExistingField={updateExistingField}
          deleteField={deleteField}
          addNewDefaultField={addNewDefaultField}
        />
        <Divider dashed />
        <Row align="middle">
          <Col span={6}>
            <Typography.Text strong>Set Additional Conditions</Typography.Text>
          </Col>
          <Col span={18}>
            <AutomationsCriteriaConditionsWrapper
              criteriaId={additionalConditionsId}
              onCriteriaChange={setAdditionalConditionsId}
              additionalConditionKeys={triggeredActivityConfig}
              builderUi={ConditionsBuilderUi}
            />
          </Col>
        </Row>
        <Divider dashed />
        <Row align="middle">
          <Col span={6}>
            <Typography.Text strong>
              {translate(
                `Choose condition(s) to remove #{${TranslationEntity.lead}}(s)
                 from this #{${TranslationEntity.automation}}`,
                [{ capitalize: true }],
              )}
            </Typography.Text>
          </Col>
          <Col span={18}>
            <AutomationsCriteriaConditionsWrapper
              criteriaId={removalConditionsId}
              onCriteriaChange={setRemovalConditionsId}
              builderUi={ConditionsBuilderUi}
              additionalConditionKeys={triggeredActivityConfig}
            />
          </Col>
        </Row>
      </Card>

      <Flex justify="flex-end" gap={16}>
        <Button onClick={() => setMenuOpen(false)}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          Set Trigger
        </Button>
      </Flex>
    </NodeMenu>
  );
}
